import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { createFilterOptions } from "@mui/material";
import type { Territoire } from "models";
import { territoiresService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { multiAutoClassNames, sortingUtil } from "utils";
import { MultiAutoComplete } from "./MultiAutoComplete";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: Territoire) => {
    const normalizedString = stringUtil.normalize(option.libelle);
    return `${normalizedString} ${option.libelle}`;
  },
});

interface TerritoireMultiAutoProps {
  value: Array<Territoire>;
  onChange?: (territoires: Array<Territoire>) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function TerritoireMultiAuto({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<TerritoireMultiAutoProps>): ReactNode {
  const [territoires, setTerritoires] = useState<Array<Territoire>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getTerritoires(): Promise<void> {
      const _territoires = await territoiresService.getAll();
      _territoires.sort(sortingUtil.sortTerritoires);
      setTerritoires(_territoires);
    }

    void catchErrors(getTerritoires);
  }, [catchErrors]);

  return (
    <MultiAutoComplete
      value={value}
      onChange={onChange}
      options={territoires}
      filterOptions={filterOptions}
      size={size}
      singleItemLabel="Territoire"
      isLoading={isLoading}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      renderOption={(props, territoire, { selected }) => (
        <li {...props} key={territoire.id}>
          <div
            className={`${multiAutoClassNames.AUTOCOMPLETE_CSS_CHECKBOX} ${selected ? multiAutoClassNames.CHECKED : multiAutoClassNames.UNCHECKED}`}
          />
          {territoire.libelle.replace("Territoire ", "")}
        </li>
      )}
    />
  );
}

export const TerritoireMultiAutoWithErrorBoundary = withComponentErrorBoundary(TerritoireMultiAuto);
